import './WeezBeeCourse.scss'
import {ChapterPanel} from "./components/ChapterPanel/ChapterPanel";
import {ContentPanel} from "./components/ContentPane/ContentPane";
import React, {memo, useCallback, useContext, useEffect, useMemo, useState} from "react";
import MenuIcon from '@mui/icons-material/Menu';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import {CourseEditorContext, COURSE_MODES} from "../../contexts/CourseEditorContext";
import {I18nReactContext} from "../../contexts/I18nContext";
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {Button} from "../Form/Button/Button";
import {buttonColors} from "../Pickers/InputTheme";
import {OrientationOverlay} from "./components/OrientationOverlay/OrientationOverlay";
import { TemplateBank } from "./components/ContentPane/ChapterEditor/TemplateBank";
import { SlideThumbnail } from "./components/SlideList/SlideThumbnail";
import { ComponentBank } from "./components/ContentPane/ChapterEditor/ComponentBank";
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
    DragOverlay
} from '@dnd-kit/core';

export const WeezBeeCourse = memo(({ courseData: externalCourseData, onExternalChapterSelected, onAddChapter, onCourseCompleted, signDocId, isSuperAdminMode = false, templateId }) => {
    // Get state directly from context
    const {
        sections,
        selectedSection,
        isFullScreen,
        setFullScreen,
        mode,
        moveSection,
        selectSection,
        addSection,
        completedSections,
        isSectionAccessible,
        markChapterCompleted
    } = useContext(CourseEditorContext) || {};

    const { i18n, isRtl } = useContext(I18nReactContext);

    // Default ChapterPanel to closed in USER_VIEW mode, open in other modes
    const [isChapterPanelVisible, setIsChapterPanelVisible] = useState(mode !== COURSE_MODES.USER_VIEW);
    const [courseCompleted, setCourseCompleted] = useState(false);
    const [isPortrait, setIsPortrait] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [showTemplateBank, setShowTemplateBank] = useState(false);
    const [pendingInsertAfterId, setPendingInsertAfterId] = useState(null);

    // Helper variable for checking if we're in any view mode (ADMIN_VIEW or USER_VIEW)
    const isViewMode = mode === COURSE_MODES.ADMIN_VIEW || mode === COURSE_MODES.USER_VIEW;

    // Configure drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 }
        })
    );

    // Handle drag start
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    // Handle drag end
    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);

        // Return if no valid drop target or if the item was dropped on itself
        if (!over || !active || active.id === over.id) return;

        // Only proceed in edit mode
        if (mode === "edit" && moveSection && sections) {
            // Find the indices of the source and destination items
            const oldIndex = sections.findIndex(chapter => chapter.id === active.id);
            const newIndex = sections.findIndex(chapter => chapter.id === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                // Call the moveSection function from context
                moveSection(oldIndex, newIndex);
            }
        }
    };

    // Note: Body class effect for fullscreen mode is now handled in CourseEditorContext
    // Note: Browser fullscreen change detection removed - ESC handling is centralized in CourseEditorContext

    // Effect to detect device orientation and mobile device
    useEffect(() => {
        const checkOrientation = () => {
            // Check if it's a mobile device
            const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            setIsMobile(isMobileDevice);

            // Only check orientation if it's a mobile device
            if (isMobileDevice) {
                const isPortraitOrientation = window.matchMedia("(orientation: portrait)").matches;
                setIsPortrait(isPortraitOrientation);
                console.log('Device orientation:', isPortraitOrientation ? 'portrait' : 'landscape');
            }
        };

        // Check orientation initially
        checkOrientation();

        // Add event listener for orientation changes
        const orientationMediaQuery = window.matchMedia("(orientation: portrait)");

        // Use the appropriate event listener based on browser support
        if (orientationMediaQuery.addEventListener) {
            orientationMediaQuery.addEventListener('change', checkOrientation);
        } else {
            // Fallback for older browsers
            orientationMediaQuery.addListener(checkOrientation);
        }

        // Clean up event listener
        return () => {
            if (orientationMediaQuery.removeEventListener) {
                orientationMediaQuery.removeEventListener('change', checkOrientation);
            } else {
                // Fallback for older browsers
                orientationMediaQuery.removeListener(checkOrientation);
            }
        };
    }, []);


    const onChapterComplete = useCallback(async (chapterId = null) => {
        // Use the provided chapterId if available, otherwise use selectedSection.id
        const idToComplete = chapterId || (selectedSection ? selectedSection.id : null);

        if (!idToComplete) {
            console.error('No chapter ID to complete');
            return;
        }

        // Skip if the course is already completed
        if (courseCompleted) {
            return;
        }

        // Call the context function to handle completion
        if (markChapterCompleted) {
            const result = await markChapterCompleted(idToComplete);

            console.log('Chapter completion result:', result);

            // Check if all chapters are completed
            if (result.success && result.allCompleted && !courseCompleted) {
                console.log('Course completed!');
                setCourseCompleted(true);
                if (onCourseCompleted) {
                    onCourseCompleted();
                }
            }
        }
    }, [selectedSection, courseCompleted, markChapterCompleted, onCourseCompleted]);

    // Automatically mark the initial chapter as completed when the course is loaded
    // For chapters with videos, we only mark them as completed after all videos have been watched
    // For chapters with questions, we only mark them as completed after the user answers correctly
    // For chapters without videos or questions, we mark them as completed automatically
    useEffect(() => {
        // Only run in USER_VIEW mode and when we have a selected section
        // Also skip if the course is already completed or the chapter is already completed
        // completedSections is now an object: { sectionId: { selectedOptionIds: [...], answeredAt: '...' } }
        if (mode !== COURSE_MODES.USER_VIEW || !selectedSection ||
            completedSections?.[selectedSection.id] || courseCompleted) {
            return;
        }

        // Check if the chapter has a video in templateData
        const hasVideoInTemplate = selectedSection.templateData && !!selectedSection.templateData.videoUrl;

        // Check if the chapter has video components in the advanced template
        const hasVideoComponents = selectedSection.template === 'advanced' &&
            selectedSection.components &&
            selectedSection.components.some(component => component.type === 'video');

        // Check if the chapter has a question
        const hasQuestion = selectedSection.question && selectedSection.question.options && selectedSection.question.options.length > 0;

        // Only mark the chapter as completed if it doesn't have any videos or questions
        if (!hasVideoInTemplate && !hasVideoComponents && !hasQuestion) {
            onChapterComplete(selectedSection.id);
        }
    }, [selectedSection, mode, completedSections, onChapterComplete, courseCompleted]);

    const onChapterSelected = useCallback(({chapterId}) => {
        // Check accessibility before navigation
        if (isSectionAccessible && !isSectionAccessible(chapterId)) {
            console.log('Section not accessible:', chapterId);
            // TODO: Optional - show toast notification
            // toast.warning('Please complete the previous section first');
            return;
        }

        if (selectSection) {
            selectSection(chapterId);
        }

        // Notify external components about the chapter selection
        if (onExternalChapterSelected && sections) {
            const chapter = sections.find(chapter => chapter.id === chapterId);
            if (chapter) {
                onExternalChapterSelected(chapter);
            }
        }
    }, [selectSection, sections, onExternalChapterSelected, isSectionAccessible]);

    const toggleChapterPanel = useCallback(() => {
        setIsChapterPanelVisible(prev => !prev);
    }, []);

    // Fullscreen toggle - delegates to context which handles browser fullscreen API
    const toggleFullscreen = useCallback(() => {
        setFullScreen(!isFullScreen);
    }, [isFullScreen, setFullScreen]);

    // Navigation functions for chapter arrows
    const navigateToPrevious = useCallback(() => {
        if (!selectedSection || !sections || sections.length === 0) return;

        const currentIndex = sections.findIndex(chapter => chapter.id === selectedSection.id);
        if (currentIndex > 0) {
            const previousChapter = sections[currentIndex - 1];
            onChapterSelected({ chapterId: previousChapter.id });
        }
    }, [selectedSection, sections, onChapterSelected]);

    const navigateToNext = useCallback(() => {
        if (!selectedSection || !sections || sections.length === 0) return;

        const currentIndex = sections.findIndex(chapter => chapter.id === selectedSection.id);
        if (currentIndex < sections.length - 1) {
            const nextChapter = sections[currentIndex + 1];
            onChapterSelected({ chapterId: nextChapter.id });
        }
    }, [selectedSection, sections, onChapterSelected]);

    // Check if navigation arrows should be shown
    const canNavigatePrevious = useMemo(() => {
        if (!selectedSection || !sections || sections.length === 0) return false;
        const currentIndex = sections.findIndex(chapter => chapter.id === selectedSection.id);
        return currentIndex > 0;
    }, [selectedSection, sections]);

    const canNavigateNext = useMemo(() => {
        if (!selectedSection || !sections || sections.length === 0) return false;
        const currentIndex = sections.findIndex(chapter => chapter.id === selectedSection.id);

        // Check if there's a next section
        if (currentIndex >= sections.length - 1) return false;

        // In USER_VIEW mode, check if the next section is accessible
        if (mode === COURSE_MODES.USER_VIEW) {
            const nextSection = sections[currentIndex + 1];
            return isSectionAccessible(nextSection.id);
        }

        // In other modes, allow navigation if next section exists
        return true;
    }, [selectedSection, sections, mode, isSectionAccessible]);

    // Add keyboard navigation support for presentation mode (arrow keys only)
    // Note: ESC key handling is centralized in CourseEditorContext
    useEffect(() => {
        if (isViewMode && isFullScreen) {
            const handleKeyPress = (event) => {
                if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                    event.preventDefault();
                    if (canNavigatePrevious) {
                        navigateToPrevious();
                    }
                } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                    event.preventDefault();
                    if (canNavigateNext) {
                        navigateToNext();
                    }
                }
                // ESC key handling removed - now centralized in CourseEditorContext
            };

            document.addEventListener('keydown', handleKeyPress);
            return () => document.removeEventListener('keydown', handleKeyPress);
        }
    }, [isViewMode, isFullScreen, canNavigatePrevious, canNavigateNext, navigateToPrevious, navigateToNext]);

    // Note: ESC key support for edit mode fullscreen is now handled in CourseEditorContext

    // Memoize the ContentPane to prevent unnecessary re-renders
    const contentPane = useMemo(() => {
        if (!selectedSection) return null;

        // Create a stable key based on chapter ID and template
        const stableKey = `chapter-${selectedSection.id}`;

        return (
            <ContentPanel
                key={stableKey}
                chapterData={selectedSection}
                onChapterComplete={onChapterComplete}
                signDocId={signDocId}
                courseCompleted={courseCompleted}
                isSuperAdminMode={isSuperAdminMode}
                templateId={templateId}
            />
        );
    }, [selectedSection, onChapterComplete, signDocId, courseCompleted, isSuperAdminMode, templateId]);

    // Function to show the template bank for creating a new chapter
    const handleAddAdvancedChapter = useCallback((afterId = null) => {
        setPendingInsertAfterId(afterId);
        setShowTemplateBank(true);
    }, []);

    // Function to handle selecting a template and creating a new chapter
    const handleSelectTemplate = useCallback((newChapter) => {
        if (addSection) {
            // Add the new chapter using the context
            addSection(newChapter, pendingInsertAfterId);
            setPendingInsertAfterId(null);
        } else if (onAddChapter) {
            // If we're not using context, fall back to the provided onAddChapter prop
            onAddChapter();
        }
        // Hide the template bank
        setShowTemplateBank(false);
    }, [addSection, onAddChapter, pendingInsertAfterId]);

    // Handle adding a component from the ComponentBank in fullscreen edit mode
    const handleAddComponent = useCallback((componentType) => {
        // Dispatch a custom event to notify the ChapterEditor to add the component
        const event = new CustomEvent('weezbee:add-component', {
            detail: { type: componentType }
        });
        window.dispatchEvent(event);
    }, []);

    // Memoize the ChapterPanel to prevent unnecessary re-renders
    // ChapterPanel now gets most values from CourseEditorContext directly
    const chapterPanel = useMemo(() => {
        return (
            <ChapterPanel
                onChapterSelected={onChapterSelected}
                selectedChapterId={selectedSection?.id}
                onAddChapter={handleAddAdvancedChapter}
            />
        );
    }, [onChapterSelected, selectedSection?.id, handleAddAdvancedChapter]);

    return (
        <div className={`WeezBeeCourse ${isFullScreen ? 'fullscreen' : ''}`}>
            {/* Template Bank Modal */}
            {showTemplateBank && (
                <div className="template-bank-modal">
                    <TemplateBank 
                        onSelectTemplate={handleSelectTemplate} 
                        onClose={() => setShowTemplateBank(false)} 
                    />
                </div>
            )}

            {/* Show orientation overlay only in view mode on mobile devices in portrait orientation */}
            {isViewMode && isMobile && isPortrait && (
                <OrientationOverlay isActive={true} />
            )}
            <>
                {mode === "edit" ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                    >
                        <div className={`chapterPanelWrapper ${!isChapterPanelVisible ? 'collapsed' : ''}`}>
                            {chapterPanel}
                        </div>
                        <DragOverlay>
                            {activeId ? (
                                <SlideThumbnail item={sections?.find(chapter => chapter.id === activeId)} width={160} isSuperAdminMode={isSuperAdminMode} />
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                ) : (
                    <div className={`chapterPanelWrapper ${!isChapterPanelVisible ? 'collapsed' : ''}`}>
                        {chapterPanel}
                    </div>
                )}
                <div className="contentPaneWrapper">
                    <div className="toggleButton" onClick={toggleChapterPanel}>
                        {isChapterPanelVisible ? <ArrowForwardIosIcon/> : <MenuIcon />}
                    </div>
                    
                    {/* Navigation buttons - shown in USER_VIEW mode (always) and other view modes (only in fullscreen) */}
                    {(isViewMode) && (
                        <div className="nav-buttons-container">
                            {/* Next Button - always on the start side (left in LTR, right in RTL) */}
                            {canNavigateNext && (
                                <button
                                    className="nav-button nav-button--next"
                                    onClick={navigateToNext}
                                    data-testid="nav-next-button"
                                    aria-label={i18n?.translate('weezbeeCourse.navigation.next') || 'Next'}
                                >
                                    <span className="nav-button__text">
                                        {i18n?.translate('weezbeeCourse.navigation.next') || 'Next'}
                                    </span>
                                    {/* In RTL, "next" points backward (left arrow), in LTR it points forward (right arrow) */}
                                    {isRtl ? (
                                        <ArrowBackIosIcon className="nav-button__icon" />
                                    ) : (
                                        <ArrowForwardIosIcon className="nav-button__icon" />
                                    )}
                                </button>
                            )}

                            {/* Back Button - always on the end side (right in LTR, left in RTL) */}
                            {canNavigatePrevious && (
                                <button
                                    className="nav-button nav-button--back"
                                    onClick={navigateToPrevious}
                                    data-testid="nav-back-button"
                                    aria-label={i18n?.translate('weezbeeCourse.navigation.back') || 'Back'}
                                >
                                   
                                    {/* In RTL, "back" points forward (right arrow), in LTR it points backward (left arrow) */}
                                    {isRtl ? (
                                        <ArrowForwardIosIcon className="nav-button__icon" />
                                    ) : (
                                        <ArrowBackIosIcon className="nav-button__icon" />
                                    )}
                                     <span className="nav-button__text">
                                        {i18n?.translate('weezbeeCourse.navigation.back') || 'Back'}
                                    </span>
                                </button>
                            )}
                        </div>
                    )}
                    
                    {contentPane}
                </div>
                {mode === "edit" && (
                    <div className="fullscreenButton">
                        <Button
                            icon={isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                            onClick={toggleFullscreen}
                            size="small"
                            variant="text"
                            color={buttonColors.YELLOW}
                        />
                    </div>
                )}

                {/* Component Bank Side Panel - shown in fullscreen edit mode */}
                {mode === "edit" && isFullScreen && (
                    <div className="fullscreen-component-bank-panel">
                        <ComponentBank onAddComponent={handleAddComponent} />
                    </div>
                )}
            </>
        </div>
    );
});
